/**
 * Custom Multi-Step Form Controller
 * Replaces the external Webflow multi-step script with our own logic
 * that understands pre-filled steps and skips them appropriately
 */

export class MultiStepController {
  private form: HTMLFormElement | null = null
  private steps: HTMLElement[] = []
  private currentStepIndex: number = 0
  private hiddenSteps: Set<number> = new Set() // 1-based step numbers (pre-fill, permanent)
  private dynamicHiddenSteps: Set<number> = new Set() // 1-based step numbers (branching, runtime)
  private debug: boolean = false

  constructor(debug: boolean = false) {
    this.debug = debug
  }

  /**
   * Initialize the multi-step form controller
   * @param hiddenSteps - Array of step numbers to skip (1-based)
   */
  public initialize(hiddenSteps: number[] = []): boolean {
    // Find the multistep form
    this.form = document.querySelector('[data-form="multistep"]') as HTMLFormElement

    if (!this.form) {
      console.warn('⚠️ Multi-step form not found')
      return false
    }

    // Get all steps
    this.steps = Array.from(this.form.querySelectorAll('[data-form="step"]')) as HTMLElement[]

    if (this.steps.length === 0) {
      console.warn('⚠️ No form steps found')
      return false
    }

    // Store hidden steps
    this.hiddenSteps = new Set(hiddenSteps)

    console.log(`🎯 Multi-step controller initialized:`)
    console.log(`   Total steps: ${this.steps.length}`)
    console.log(`   Hidden steps: [${Array.from(this.hiddenSteps).join(', ')}]`)

    // Find first visible step
    this.currentStepIndex = this.findFirstVisibleStep()

    // Setup navigation
    this.setupNavigation()

    // Show first visible step
    this.showStep(this.currentStepIndex)

    return true
  }

  /**
   * Find the first step that's not hidden
   */
  private findFirstVisibleStep(): number {
    for (let i = 0; i < this.steps.length; i++) {
      const stepNumber = i + 1 // Convert to 1-based
      if (!this.hiddenSteps.has(stepNumber)) {
        return i
      }
    }
    return 0 // Fallback to first step
  }

  /**
   * Setup navigation event listeners
   */
  private setupNavigation(): void {
    // Use event delegation on document to catch all clicks
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement

      // Check if the clicked element or its parent is a button
      const button = target.closest('[data-form="next-btn"], [data-form="back-btn"], [data-form="submit-btn"]') as HTMLElement

      if (!button) return

      // The additional-info outcome form is ALSO a data-form="multistep" form,
      // but it's driven by AdditionalInfoFormController as a single-submit form
      // (see src/additional-info-form/). This controller only owns the MAIN lead
      // form. Without this guard, our document-level capture handler would
      // stopImmediatePropagation() the additional-info Submit/Next/Back clicks
      // before that form's own bubble-phase handlers run — dead submit. Ignore
      // any button inside the additional-info host and let it handle itself.
      if (button.closest('[data-component="cd-outcome-additional-information"]')) {
        return
      }

      const dataForm = button.getAttribute('data-form')

      if (dataForm === 'next-btn') {
        console.log('🔵 Next button clicked - handling navigation')
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        this.handleNext()
        return false
      }

      if (dataForm === 'back-btn') {
        console.log('🔴 Back button clicked - handling navigation')
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        this.handleBack()
        return false
      }

      if (dataForm === 'submit-btn') {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        if (this.findNextVisibleStep() !== -1) {
          // Not on last step yet - advance through remaining steps
          console.warn('⚠️ Submit clicked but not on last step - advancing instead')
          this.handleNext()
        } else {
          // On last step - dispatch submit event so lead tracker can process it
          console.log('✅ Submit button clicked on last step - dispatching form submit')
          this.submitForm()
        }
        return false
      }
    }, true) // Capture phase

    // ALSO prevent form submission via native submit (not button click)
    this.form?.addEventListener('submit', (e) => {
      console.log('⚠️  Form submit event detected - preventing default')
      e.preventDefault()
      e.stopPropagation()
      return false
    }, true)

    console.log('✅ Navigation handlers attached via event delegation')
  }

  /**
   * Check if element is a Next button
   */
  private isNextButton(element: HTMLElement): boolean {
    const dataForm = element.getAttribute('data-form')
    // Check various possible selectors (Webflow uses data-form="next-btn")
    return !!(
      (element.hasAttribute('data-form') && (dataForm === 'next' || dataForm === 'next-btn')) ||
      element.classList.contains('next-button') ||
      element.classList.contains('button-next') ||
      element.textContent?.toLowerCase().includes('next') ||
      element.closest('[data-form="next"]') ||
      element.closest('[data-form="next-btn"]')
    )
  }

  /**
   * Check if element is a Back button
   */
  private isBackButton(element: HTMLElement): boolean {
    const dataForm = element.getAttribute('data-form')
    // Check various possible selectors (Webflow uses data-form="back-btn")
    return !!(
      (element.hasAttribute('data-form') && (dataForm === 'back' || dataForm === 'back-btn')) ||
      element.classList.contains('back-button') ||
      element.classList.contains('button-back') ||
      element.textContent?.toLowerCase().includes('back') ||
      element.closest('[data-form="back"]') ||
      element.closest('[data-form="back-btn"]')
    )
  }

  /**
   * Handle Next button click
   */
  private handleNext(): void {
    if (this.debug) {
      console.log(`📍 Next clicked from step ${this.currentStepIndex + 1}`)
    }

    // Validate current step
    const validationError = this.validateCurrentStep()
    if (validationError) {
      console.warn(`⚠️ Current step validation failed: ${validationError}`)
      this.showValidationError(validationError)
      return
    }

    // Clear any previous dynamic branching and re-evaluate for this step
    this.dynamicHiddenSteps.clear()

    // Check for data-go-to branching (e.g. "No co-applicant" radio)
    const gotoTarget = this.getGotoTarget()
    if (gotoTarget !== null) {
      // Hide all steps from the one after current through gotoTarget inclusive
      for (let i = this.currentStepIndex + 1; i <= gotoTarget; i++) {
        this.dynamicHiddenSteps.add(i + 1) // Convert to 1-based
      }
      console.log(`🔀 data-go-to=${gotoTarget}: dynamically hiding steps ${this.currentStepIndex + 2}–${gotoTarget + 1}`)
    }

    // Find next visible step
    const nextIndex = this.findNextVisibleStep()

    if (nextIndex === -1) {
      // No more steps - submit form
      console.log('✅ Last step reached - submitting form')
      this.submitForm()
      return
    }

    // Navigate to next step
    this.showStep(nextIndex)

    // Notify lead tracker that Next was clicked (bubble-phase handlers are blocked by stopImmediatePropagation)
    document.dispatchEvent(new CustomEvent('multiStepNext', { detail: { stepIndex: nextIndex } }))
  }

  /**
   * Handle Back button click
   */
  private handleBack(): void {
    if (this.debug) {
      console.log(`📍 Back clicked from step ${this.currentStepIndex + 1}`)
    }

    // Find previous visible step
    const prevIndex = this.findPreviousVisibleStep()

    if (prevIndex === -1) {
      console.warn('⚠️ Already at first step')
      return
    }

    // Navigate to previous step
    this.showStep(prevIndex)
  }

  /**
   * Find next visible step (checks both permanent and dynamic hidden steps)
   */
  private findNextVisibleStep(): number {
    for (let i = this.currentStepIndex + 1; i < this.steps.length; i++) {
      const stepNumber = i + 1
      if (!this.hiddenSteps.has(stepNumber) && !this.dynamicHiddenSteps.has(stepNumber)) {
        return i
      }
    }
    return -1 // No more visible steps
  }

  /**
   * Find previous visible step (checks both permanent and dynamic hidden steps)
   */
  private findPreviousVisibleStep(): number {
    for (let i = this.currentStepIndex - 1; i >= 0; i--) {
      const stepNumber = i + 1
      if (!this.hiddenSteps.has(stepNumber) && !this.dynamicHiddenSteps.has(stepNumber)) {
        return i
      }
    }
    return -1 // No previous visible steps
  }

  /**
   * Get data-go-to target from any selected radio in the current step
   * Returns 0-based step index, or null if no data-go-to found on a selected radio
   */
  private getGotoTarget(): number | null {
    const currentStep = this.steps[this.currentStepIndex]
    if (!currentStep) return null

    const selectedRadios = Array.from(
      currentStep.querySelectorAll('input[type="radio"][data-go-to]:checked')
    ) as HTMLInputElement[]

    for (const radio of selectedRadios) {
      const gotoAttr = radio.getAttribute('data-go-to')
      if (gotoAttr !== null) {
        const gotoIndex = parseInt(gotoAttr, 10)
        if (!isNaN(gotoIndex)) {
          return gotoIndex
        }
      }
    }

    return null
  }

  /**
   * Show a specific step
   */
  private showStep(index: number): void {
    const stepNumber = index + 1

    if (this.debug) {
      console.log(`🎯 Showing step ${stepNumber} (index ${index})`)
    }

    // Hide all steps
    this.steps.forEach(step => {
      step.style.display = 'none'
      step.setAttribute('aria-hidden', 'true')
    })

    // Show target step
    const targetStep = this.steps[index]
    if (targetStep) {
      targetStep.style.display = ''
      targetStep.removeAttribute('aria-hidden')
      this.currentStepIndex = index

      console.log(`✅ Now showing step ${stepNumber}`)

      // Manage button visibility based on position in the flow
      this.updateButtonVisibility()

      // Update progress if needed
      this.updateProgress()
    }
  }

  /**
   * Show/hide Next, Back, and Submit buttons based on current position
   * Replaces the button management that Webflow's multi-step script previously handled
   */
  private updateButtonVisibility(): void {
    if (!this.form) return

    const isFirst = this.findPreviousVisibleStep() === -1
    const isLast = this.findNextVisibleStep() === -1

    const submitBtn = this.form.querySelector('[data-form="submit-btn"]') as HTMLElement | null
    const nextBtn = this.form.querySelector('[data-form="next-btn"]') as HTMLElement | null
    const backBtn = this.form.querySelector('[data-form="back-btn"]') as HTMLElement | null

    if (nextBtn) {
      nextBtn.style.display = isLast ? 'none' : 'block'
    }
    if (submitBtn) {
      submitBtn.style.display = isLast ? 'block' : 'none'
    }
    if (backBtn) {
      backBtn.style.display = isFirst ? 'none' : 'block'
    }

    console.log(`🔘 Buttons: next=${isLast ? 'hidden' : 'visible'}, submit=${isLast ? 'visible' : 'hidden'}, back=${isFirst ? 'hidden' : 'visible'}`)
  }

  /**
   * Validate current step
   * @returns null if valid, or an error message string if invalid
   */
  private validateCurrentStep(): string | null {
    const currentStep = this.steps[this.currentStepIndex]
    if (!currentStep) return null

    // Get all required inputs in current step
    const requiredInputs = Array.from(
      currentStep.querySelectorAll('input[required], select[required], textarea[required]')
    ) as (HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[]

    // Check each required input
    for (const input of requiredInputs) {
      if (input instanceof HTMLInputElement && input.type === 'checkbox') {
        if (!input.checked) {
          const label = input.closest('.w-checkbox')?.querySelector('.w-form-label')?.textContent?.trim()
          return label
            ? `Please check the required box to continue.`
            : 'Please check all required boxes before continuing.'
        }
      } else {
        if (!input.value || input.value.trim() === '') {
          return 'Please fill in all required fields before continuing.'
        }
      }
    }

    return null
  }

  /**
   * Show a validation error message near the current step's Next button
   */
  private showValidationError(message: string): void {
    if (!this.form) return

    // Remove any existing error
    const existing = this.form.querySelector('.multistep-validation-error')
    if (existing) existing.remove()

    const errorEl = document.createElement('div')
    errorEl.className = 'multistep-validation-error'
    errorEl.style.cssText = 'color:#E53E3E;font-size:14px;margin:8px 0;font-weight:500;'
    errorEl.textContent = message

    // Insert before the Next button
    const nextBtn = this.form.querySelector('[data-form="next-btn"]') as HTMLElement | null
    if (nextBtn?.parentElement) {
      nextBtn.parentElement.insertBefore(errorEl, nextBtn)
    }

    // Auto-remove after 5 seconds
    setTimeout(() => errorEl.remove(), 5000)
  }

  /**
   * Submit the form
   * Dispatches a custom event instead of the native 'submit' event to bypass
   * Webflow's built-in form handling which would reset the multi-step form
   */
  private submitForm(): void {
    console.log('📤 Dispatching multiStepSubmit event...')
    document.dispatchEvent(new CustomEvent('multiStepSubmit'))
  }

  /**
   * Update progress indicator (optional)
   */
  private updateProgress(): void {
    // Calculate progress
    const visibleSteps = this.steps.length - this.hiddenSteps.size
    const completedSteps = Array.from({ length: this.currentStepIndex + 1 })
      .filter((_, i) => !this.hiddenSteps.has(i + 1))
      .length

    const progressPercent = (completedSteps / visibleSteps) * 100

    if (this.debug) {
      console.log(`📊 Progress: ${completedSteps}/${visibleSteps} steps (${progressPercent.toFixed(0)}%)`)
    }

    // Update progress bar if it exists
    const progressBar = document.querySelector('.form-progress-bar') as HTMLElement
    if (progressBar) {
      progressBar.style.width = `${progressPercent}%`
    }
  }
}
